import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { NavLink } from "react-router-dom";
import { AGENT_BREADCRUMBS } from "../../components/apps/agent-management/constants";
import LineNameLabel from "../../components/common/LineNameLabel";
import {
  AgentsManagementTable,
  CreateAgent,
  ChangeAgentPasswordModal,
  EditAgentInformation,
  AgentCreatedCredentials,
  AgentGroupsSection,
} from "../../components/apps/agent-management";
import { getAgentsFromApi } from "../../actions/agentes";
import {
  getAgentsWithFilters,
} from "../../actions/agents/get-agents.actions";
import {
  changeAgentStatus,
  updateAgentInformation,
  resetAgentPassword,
} from "../../actions/agents/change-agent-info.actions";
import { getAgentManagementPermissions } from "../../components/apps/agent-management/permissions";
import { useToken } from "../../hooks/useToken";
import { useDispatch, useSelector } from "react-redux";

export const AgentManagement = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [openModal, setOpenModal] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [agentToEdit, setAgentToEdit] = useState(null);
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [agentToChangePassword, setAgentToChangePassword] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [agentsOverride, setAgentsOverride] = useState(null);
  const [statusUpdatingIds, setStatusUpdatingIds] = useState([]);
  const [accessUpdatingIds, setAccessUpdatingIds] = useState([]);
  const [roleFilter, setRoleFilter] = useState("todos");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [filterLoading, setFilterLoading] = useState(false);
  const [openCredentialsModal, setOpenCredentialsModal] = useState(false);
  const [agentCredentials, setAgentCredentials] = useState(null);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const { agents } = useSelector((state) => state.agentes);
  const agent = useSelector((state) => state.agent);
  const { getTokenSilent } = useToken();
  const dispatch = useDispatch();

  const subrol = agent?.subrole || "";
  const permissions = getAgentManagementPermissions(subrol);

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({
      open: true,
      severity,
      message,
    });
  }, []);

  const loadAgents = useCallback(async () => {
    try {
      const accessToken = await getTokenSilent();
      await dispatch(getAgentsFromApi(agent.id_customer, accessToken));
    } catch (error) {
      console.error("Error cargando agentes:", error);
      showSnackbar("Error al recargar agentes", "error");
    }
  }, [dispatch, agent.id_customer, getTokenSilent, showSnackbar]);

  const loadAgentsWithFilters = useCallback(async () => {
    if (!agent.id_customer) return;
    setFilterLoading(true);
    try {
      const accessToken = await getTokenSilent();
      const result = await getAgentsWithFilters({
        id_customer: agent.id_customer,
        token: accessToken,
        role: roleFilter,
        status: statusFilter,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      setAgentsOverride(result.items || []);
      setTotal(result.total || 0);
    } catch (error) {
      console.error("Error cargando agentes con filtros:", error);
      showSnackbar("Error al cargar agentes con filtros", "error");
      setAgentsOverride([]);
      setTotal(0);
    } finally {
      setFilterLoading(false);
    }
  }, [
    agent.id_customer,
    roleFilter,
    statusFilter,
    pageSize,
    page,
    getTokenSilent,
    showSnackbar,
  ]);

  useEffect(() => {
    if (Object.keys(agents).length === 0) {
      loadAgents();
    }
  }, [agents, loadAgents]);

  useEffect(() => {
    if (roleFilter !== "todos" || statusFilter !== "todos") {
      setPage(1);
    }
  }, [roleFilter, statusFilter]);

  useEffect(() => {
    if (agent.id_customer && !searchResult) {
      loadAgentsWithFilters();
    }
  }, [
    page,
    pageSize,
    roleFilter,
    statusFilter,
    agent.id_customer,
    loadAgentsWithFilters,
    searchResult,
  ]);

  const handleOpenModal = () => {
    if (!permissions.canCreateAgent) return;
    setOpenModal(true);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const handleEditAgent = (agent) => {
    setAgentToEdit(agent);
    setOpenEditModal(true);
  };

  const handleOpenChangePassword = (agentItem) => {
    if (!permissions.canChangePassword) return;
    setAgentToChangePassword(agentItem);
    setOpenPasswordModal(true);
  };

  const handleResetPassword = async (agentItem) => {
    if (!permissions.canChangePassword || !agentItem?._id) return;

    setIsResettingPassword(true);
    try {
      const token = await getTokenSilent();
      const result = await resetAgentPassword({
        id_agent: agentItem._id,
        token,
      });
      setAgentCredentials(result);
      setOpenCredentialsModal(true);
      setOpenPasswordModal(false);
      setAgentToChangePassword(null);
    } catch (error) {
      console.error("Error al restablecer contraseña:", error);
      showSnackbar("Error al restablecer contraseña", "error");
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleAgentUpdated = (updatedAgent) => {
    if (Array.isArray(searchResult)) {
      setSearchResult((prev) =>
        (prev || []).map((item) =>
          item._id === updatedAgent._id ? updatedAgent : item,
        ),
      );
    } else if (agentsOverride) {
      setAgentsOverride((prev) =>
        (prev || []).map((item) =>
          item._id === updatedAgent._id ? updatedAgent : item,
        ),
      );
    }
  };

  const handleAgentDeleted = ({ deletedAgentId, response }) => {
    if (Array.isArray(searchResult)) {
      setSearchResult((prev) =>
        (prev || []).filter((item) => item._id !== deletedAgentId),
      );
    }

    setAgentsOverride((prev) =>
      (prev || []).filter((item) => item._id !== deletedAgentId),
    );

    const message = response?.message || "Agente eliminado correctamente";
    showSnackbar(message, "success");
    loadAgents();
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    // clear previous search result while typing
    setSearchResult(null);
    setSearchPerformed(false);
  };

  const handleRoleFilterChange = (value) => {
    setRoleFilter(value);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
  };

  const handleSearchSubmit = async (searchValue) => {
    if (!searchValue) return;
    setSearchLoading(true);
    setSearchPerformed(true);
    try {
      const token = await getTokenSilent();
      setPage(1);
      const result = await getAgentsWithFilters({
        id_customer: agent.id_customer,
        token,
        search: searchValue,
        limit: pageSize,
        offset: 0,
      });

      setSearchResult(result.items || []);
      setTotal(result.total || 0);
    } catch (error) {
      console.error("Error buscando agentes:", error);
      setSearchResult([]);
      showSnackbar("Error al buscar agentes", "error");
    } finally {
      setSearchLoading(false);
    }
  };

  const handleChangeAgentStatus = async (agentItem) => {
    if (!permissions.canChangeAgentStatus || !agentItem?._id) return;

    const currentStatus = agentItem.agent_disponibility;
    const nextStatus = currentStatus === "Activo" ? "Inactivo" : "Activo";
    setStatusUpdatingIds((prev) => [...prev, agentItem._id]);

    try {
      const token = await getTokenSilent();
      await changeAgentStatus({
        agentId: agentItem._id,
        status: nextStatus,
        token,
      });

      if (Array.isArray(searchResult)) {
        setSearchResult((prev) =>
          (prev || []).map((item) =>
            item._id === agentItem._id
              ? { ...item, agent_disponibility: nextStatus }
              : item,
          ),
        );
      } else {
        setAgentsOverride((prev) =>
          (prev || []).map((item) =>
            item._id === agentItem._id
              ? { ...item, agent_disponibility: nextStatus }
              : item,
          ),
        );
      }

      showSnackbar("Estado del agente actualizado", "success");
    } catch (error) {
      console.error("Error al cambiar estado de agente:", error);
      showSnackbar("Error al cambiar el estado del agente", "error");
    } finally {
      setStatusUpdatingIds((prev) => prev.filter((id) => id !== agentItem._id));
    }
  };

  const handleChangeAgentAccess = async (agentItem) => {
    if (!permissions.canEditAgent || !agentItem?._id) return;

    const nextAccess = !agentItem?.is_active;
    setAccessUpdatingIds((prev) => [...prev, agentItem._id]);

    try {
      const token = await getTokenSilent();
      await updateAgentInformation({
        id_agent: agentItem._id,
        token,
        payload: { is_active: nextAccess },
      });

      if (Array.isArray(searchResult)) {
        setSearchResult((prev) =>
          (prev || []).map((item) =>
            item._id === agentItem._id
              ? { ...item, is_active: nextAccess }
              : item,
          ),
        );
      } else {
        setAgentsOverride((prev) =>
          (prev || []).map((item) =>
            item._id === agentItem._id
              ? { ...item, is_active: nextAccess }
              : item,
          ),
        );
      }

      showSnackbar("Acceso del agente actualizado", "success");
    } catch (error) {
      console.error("Error al cambiar acceso del agente:", error);
      showSnackbar("Error al cambiar el acceso del agente", "error");
    } finally {
      setAccessUpdatingIds((prev) => prev.filter((id) => id !== agentItem._id));
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const isServerPaginated = true;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100vw",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
        px: 1,
      }}
    >
      <Box
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 4,
            flex: "0 0 auto",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 700 }}>
              <LineNameLabel/>
            </Typography>

            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              aria-label="breadcrumb"
            >
              {AGENT_BREADCRUMBS.map((item) => (
                <Box key={item.title}>
                  {item.to ? (
                    <Link
                      underline="none"
                      color="inherit"
                      component={NavLink}
                      to={item.to}
                    >
                      {item.title}
                    </Link>
                  ) : (
                    <Typography color="textPrimary">{item.title}</Typography>
                  )}
                </Box>
              ))}
            </Breadcrumbs>
          </Box>

          {activeTab === 0 && (
            <Button
              sx={{ mt: 2 }}
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              disabled={!permissions.canCreateAgent}
            >
              Crear Agente
            </Button>
          )}
        </Box>

        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          sx={{
            mb: 2,
            borderBottom: 1,
            borderColor: "divider",
            flex: "0 0 auto",
          }}
        >
          <Tab label="Agentes" />
          <Tab label="Grupos de agentes" />
        </Tabs>

        <Box sx={{ flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}>
          {activeTab === 1 ? (
            <AgentGroupsSection canManage={permissions.canManageGroups} />
          ) : (
            <AgentsManagementTable
              agents={Array.isArray(searchResult) ? searchResult : agentsOverride || []}
              loading={searchLoading || filterLoading}
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onEditAgent={handleEditAgent}
              onChangeAgentStatus={handleChangeAgentStatus}
              onChangeAgentAccess={handleChangeAgentAccess}
              onChangePassword={handleOpenChangePassword}
              statusUpdatingIds={statusUpdatingIds}
              accessUpdatingIds={accessUpdatingIds}
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              onSearchSubmit={handleSearchSubmit}
              searchPerformed={searchPerformed}
              roleFilter={roleFilter}
              statusFilter={statusFilter}
              onRoleFilterChange={handleRoleFilterChange}
              onStatusFilterChange={handleStatusFilterChange}
              canChangeAgentStatus={permissions.canChangeAgentStatus}
              canEditAgent={permissions.canEditAgent}
              canChangePassword={permissions.canChangePassword}
              serverPagination={isServerPaginated}
            />
          )}
        </Box>
      </Box>

      <CreateAgent
        open={openModal}
        onClose={() => setOpenModal(false)}
        onCreated={async (createdAgent) => {
          setOpenModal(false);
          setAgentCredentials(createdAgent);
          setOpenCredentialsModal(true);
          showSnackbar("Agente creado exitosamente", "success");
          try {
            const token = await getTokenSilent();
            dispatch(getAgentsFromApi(agent.id_customer, token));
          } catch (err) {
            console.error("Error recargando agentes:", err);
          }
        }}
      />

      <EditAgentInformation
        open={openEditModal}
        onClose={() => {
          setOpenEditModal(false);
          setAgentToEdit(null);
        }}
        agent={agentToEdit}
        onAgentUpdated={handleAgentUpdated}
        onAgentDeleted={handleAgentDeleted}
        agentsForReassign={agents?.items || []}
        onNotify={showSnackbar}
        canEdit={permissions.canEditAgent}
      />

      <ChangeAgentPasswordModal
        open={openPasswordModal}
        onClose={() => {
          setOpenPasswordModal(false);
          setAgentToChangePassword(null);
        }}
        agent={agentToChangePassword}
        onResetPassword={handleResetPassword}
        isResetting={isResettingPassword}
      />

      <AgentCreatedCredentials
        open={openCredentialsModal}
        onClose={() => {
          setOpenCredentialsModal(false);
          setAgentCredentials(null);
        }}
        credentials={agentCredentials}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
